// @flow

import { TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import { DatePicker } from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DataGridWrapper from "../../../components/mui/grids/dataGridWrapper/dataGridWrapper";
import { API } from "../../../helpers/constants";
import ExportExcelFromUrl from "../../../components/mui/app/exportExcelFromUrl/exportExcelFromUrl";
import { access_skills, access_user } from "../../../helpers/rights";
import { useMuiGridServerSide } from "../../../hooks/muiGrid/useMuiGridServerSide";
import useUserData from "../../../hooks/user/useUserData";
import { withPermissionHOC } from "../../permission/components/permissionHOC/permissionHOC";
import { WorkerGridContainer } from "../components/WorkerGridContainer";
import { REPORTS } from "../locales/namespaces";
import CustomAutocomplete from "../../../components/mui/form/autocomplete/autocomplete";

// Μπορείς να δηλώσεις τις αρχικές τιμές των ημερομηνιών εκτός του component σαν σταθερές,
// για να τις χρησιμοποιείς όπου χρειάζονται, εφόσον δεν αλλάζουν με βάση κάποιο prop ή state
// ούτε επηρεάζουν το lifecycle του component
const initialStartDate = dayjs().startOf('month')
const initialEndDate = dayjs()

const WorkersReport = (props: Props) => {
	const { language, dateFormat } = useUserData()
	const { t } = useTranslation(REPORTS)
	const [selectedStartDate, setSelectedStartDate] = useState(initialStartDate)
	const [selectedEndDate, setSelectedEndDate] = useState(initialEndDate)
	const [projectId, setProjectId] = useState(null)

	const {
		isLoading,
		data,
		total,
		page,
		pageSize,
		filterModel,
		sortModel,
		onPaginationModelChange,
		onSortModelChange,
		onFilterModelChange,
		dataManager,
		response
	} = useMuiGridServerSide({
		url: "fts/reports",
		initialPageSize: 50,
		initialSortModel: [{ name: "id", direction: "descending" }],
		otherDataParams: {
			reportType: 2,
			...(selectedStartDate && selectedStartDate?.isValid() && { StartDt: dayjs(selectedStartDate).format('YYYY-MM-DD') }), // Στείλε το φίλτρο μόνο αν υπάρχει η ημερομηνία. Αν υπάρχει στείλε το με το format της βάσης
			...(selectedEndDate && selectedEndDate?.isValid() && { EndDt: dayjs(selectedEndDate).format('YYYY-MM-DD') }),
			ProjectId: projectId?.id || ''
		}
	});
	
	// ημερομηνίες
	const handleStartDateChange = (newValue) => {
		setSelectedStartDate(newValue);
	}
	
	const handleEndDateChange = (newValue) => {
		setSelectedEndDate(newValue);
	}

	console.log('response',response.projects)
	return (
		<Grid
			container
			spacing={1}
			sx={{ marginLeft: '0px', marginTop: '10px', alignItems: 'center' }}
		>
			<Grid size={{ xs: 12, sm: 6, md: 2 }}>
				<DatePicker
					slotProps={{
						textField: {
							size: 'small',
							variant: 'standard'
						}
					}}
					name="StartDt"
					views={['year', 'month', 'day']}
					inputFormat={'DD-MM-YYYY'}
					format={dateFormat}
					value={selectedStartDate}
					onChange={handleStartDateChange}
				/>
			</Grid>
			<Grid size={{ xs: 12, sm: 6, md: 2 }}>
				<DatePicker
					slotProps={{
						textField: {
							size: 'small',
							variant: 'standard'
						}
					}}
					name="EndDt"
					views={['year', 'month', 'day']}
					inputFormat={'DD-MM-YYYY'}
					format={dateFormat}
					value={selectedEndDate}
					onChange={handleEndDateChange}
				/>
			</Grid>
			<Grid size={{ xs: 12, sm: 6, md: 2 }}>
				<CustomAutocomplete
					identifier="id"
					value={projectId}
					onChange={(e,v)=> {
						setProjectId(v)
						console.log('v',v)
					}}
					options={response?.projects || []}
					labelKey={'Description'}
				/>
			</Grid>
			<Grid size={{ xs: 10, sm: 6, md: 4 }}>
				<Button
					variant="outlined"
					color="success"
					onClick={() => {
						/*
						 * Στο click της αναζήτησης καλείς τη συνάρτηση που τρέχει την κλήση των
						 * δεδομένων του grid και της περνάς σαν παράμετρο ένα object, το οποίο
						 * έχει τις τιμές που έχουν τη στιγμή του click τα φίλτρα.
						*/
						dataManager({
							...(selectedStartDate && selectedStartDate?.isValid() && { StartDt: dayjs(selectedStartDate).format('YYYY-MM-DD') }),
							...(selectedEndDate && selectedEndDate?.isValid() && { EndDt: dayjs(selectedEndDate).format('YYYY-MM-DD') })
						})
					}}
				>
					{t('Search')}
				</Button>
				{/*Δίνει στον χρήστη τη δυνατότητα να καθαρίσει την αναζήτησή του στις αρχικές τιμές. */}
				<Button
					sx={{marginLeft: 1}}
					variant="outlined"
					color="error"
					onClick={() => {
						/*
						 * Για να φέρει τα αρχικά δεδομένα το grid τρέχεις τον dataManager
						 * στέλνοντας σαν φίλτρα τις αρχικές τιμές που είχες στις ημερομηνίες.
						*/
						const params = {
							StartDt: dayjs(initialStartDate).format('YYYY-MM-DD'),
							EndDt: dayjs(initialEndDate).format('YYYY-MM-DD')
						}
						// Αν πετύχει η κλήση που ανανεώνει τα data, επανέφερε και στις αρχικές τιμές τα φίλτρα.
						const onSuccess = () => {
							setSelectedEndDate(initialEndDate)
							setSelectedStartDate(initialStartDate)
						}
						dataManager(params, onSuccess)
					}}
				>
					{t('Clear')}
				</Button>
			</Grid>
			<Grid size={{ xs: 2, sm: 6, md: 1 }}>
				<Box sx={{ textAlign: 'right' }}>
					<ExportExcelFromUrl url={`${API}/en/fts/reports/export`} data={{
						isActive: 0,
						reportType: 2,
						StartDt : dayjs(selectedStartDate).format('YYYY-MM-DD'),
						EndDt : dayjs(selectedEndDate).format('YYYY-MM-DD'),
						Filters: filterModel?.[0]?.predicates,
						Sort: sortModel?.[0],
					}}/>
				</Box>
			</Grid>
			<Grid size={{ md: 12 }}>
				<DataGridWrapper sx={{ height: 600 }}>
					<WorkerGridContainer
						data={data}
						isLoading={isLoading}
						page={page}
						pageSize={pageSize}
						onPaginationModelChange={onPaginationModelChange}
						total={total}
						onSortModelChange={onSortModelChange}
						onFilterModelChange={onFilterModelChange}
						language={language}
						dateFormat={dateFormat}
						selectedStartDate={selectedStartDate}
						selectedEndDate={selectedEndDate}
						
					/>
				</DataGridWrapper>
			</Grid>
		</Grid>
	);
};

// export default WorkersReport
export default withPermissionHOC(WorkersReport, [access_skills])